<template>

  <div>

    <v-row>

      <v-col>
        <date-range-field
          :label="$t('titles.dates')"
          v-model="formState.dateRange"
          :rules="[rules!.notEmpty!('Required'), maxDateRange, atLeastOneDowIsEnabled]"
          :min="today.toString()"
        />
      </v-col>

      <v-col>
        <template v-if="formState.dateRange">
          <template v-if="checkingForSpotRates">
            <p class="mt-4">
              <v-progress-circular indeterminate size="small" />
              Checking for existing spot rates&hellip;
            </p>
          </template>
          <template v-else>
            <p v-if="existingParentSpotRateCount" class="mt-4">
              <v-icon icon="$warning" />
              {{ existingParentSpotRateCount }} existing spot {{ existingParentSpotRateCount > 1 ? 'rates' : 'rate' }} will be overwritten.
            </p>
            <p v-if="!existingParentSpotRateCount" class="mt-4">
              <v-icon icon="$ok" />
              No existing spot rates found
            </p>
          </template>
        </template>
      </v-col>

    </v-row>

    <v-table density="compact">
      <thead>
        <tr>
          <th scope="col" class="w-15">
            {{ $t('titles.channel_facility') }}
          </th>
          <th v-for="defaultRate in defaultRates" :key="defaultRate.ID" :style="{width: `${Math.floor(85 / defaultRates.length)}%`}">
            <template v-if="productHasTieredRates">
              <template v-if="defaultRate.perQuantity && defaultRate.perUnit">
                {{ defaultRate.perQuantity }} {{ defaultRate.perUnit }}{{ defaultRate.perQuantity > 1 ? 's': '' }} <br>
              </template>
            </template>
            <template v-else>
              {{ $t('titles.cost') }}
            </template>
          </th>
        </tr>
      </thead>
      <tfoot>
        <tr>
          <td :colspan="productHasTieredRates ? defaultRates.length + 1 : 2">
            <v-btn
              :text="$t('actions.set_all_to_default')"
              @click="setAllToDefault()"
              density="compact"
              class="my-4"
            />
          </td>
        </tr>
      </tfoot>
      <tbody>
        <tr v-for="channelFacility in formState.channelFacilities">
          <td>
            {{ ChannelRegistry.findTitle(channelFacility.channel) }}
            ({{ channelFacility.exChannelId }})
          </td>
          <td v-for="defaultRate in defaultRates">
            <v-number-input
              v-if="channelFacility.channelDefaultRates.has(defaultRate.ID)"
              v-model="channelFacility.channelDefaultRates.get(defaultRate.ID)!.cost"
              prefix="$"
              :precision="2"
              :min="0"
              density="compact"
              :rules="[rules!.required!(), rules!.min!(0.01), (value: any) => (channelFacility.channel === 'spothero' && value > 250) ? 'SpotHero rates can\'t exceed $250' : true]"
              class="my-2"
            />
          </td>
        </tr>
      </tbody>
    </v-table>

  </div>

</template>

<script setup lang="ts">

import { computed, onBeforeMount, reactive, ref, watchEffect } from 'vue'
import { computedAsync } from '@vueuse/core'
import { type DateInstance, useDate } from 'vuetify'
import { useRules } from 'vuetify/labs/rules'

// components
import DateRangeField from '../../../../forms/DateRangeField.vue'

// models
import { DefaultRate, type GetRatesByLocationIdResponse } from '../../../../../models/DefaultRate'
import Location from '../../../../../models/Location'
import LocationProduct from '../../../../../models/LocationProduct'

// misc
import type { DateRange } from '../../../../../types/misc'
import VerticalRegistry from '../../../../../verticals/VerticalRegistry'
import ChannelRegistry from '../../../../../channels/ChannelRegistry'
import RatesApi, {
  type SyncSpotRatesRequest,
  type SyncSpotRatesRequestItem
} from '../../../../../services/api-clients/RatesApi'
import { range } from 'underscore'
import NotificationService from '../../../../../services/NotificationService'
import { Temporal } from '@js-temporal/polyfill'
import type AbstractVertical from '../../../../../verticals/AbstractVertical'

/**
 * Props
 */

interface Props {
  location: Location,
  locationProduct: LocationProduct
  dateRange?: null | DateRange
}

const props = withDefaults(defineProps<Props>(), {
  dateRange: null
})

const emit = defineEmits(['submitted'])
const vuetifyDateHelper = useDate()
const rules = useRules()

/**
 * Initialization
 */

onBeforeMount(() => {
  if (props.dateRange) {
    formState.dateRange = props.dateRange
  }
})

/**
 * Info about the selected product
 */

const productVertical = computed<null | AbstractVertical>(() => {
  return VerticalRegistry.get(props.locationProduct.product.verticalType) ?? null
})

const productHasTieredRates = computed<boolean>(() => {
  return productVertical.value?.supports(['tieredRates']) ?? false
})

const productIsLimitedByWeekday = computed<boolean>(() => {
  return productVertical.value?.supports(['limitedByWeekday']) ?? false
})

/**
 * Form state
 */

interface FormState {
  dateRange: null | DateRange
  channelFacilities: FormChannelFacility[]
}

interface FormChannelFacility {
  readonly id: number
  readonly channel: string
  readonly exChannelId: null | string
  channelDefaultRates: Map<number, FormCdr> // keyed by default rate ID
}

interface FormCdr {
  readonly id: number
  readonly defaultCost: number
  readonly channelCost: number
  cost: null | number
}

const formState = reactive<FormState>({
  dateRange: null,
  channelFacilities: [],
})

const dates = computed<Temporal.PlainDate[]>(() => {
  if (!formState.dateRange) {
    return []
  }
  const numDays = formState.dateRange.start.until(formState.dateRange.end).days + 1
  return range(0, numDays).map(i => formState.dateRange!.start.add({days: i}))
})

function maxDateRange(vuetifyDates: DateInstance[]) {
  return vuetifyDates.length <= 30 || 'Can\'t be longer than 30 days'
}

function atLeastOneDowIsEnabled(vuetifyDates: DateInstance[]) {

  if (!vuetifyDates.length) {
    return true
  }

  if (!productIsLimitedByWeekday.value) {
    return true
  }

  const vuetifyStart = vuetifyDates.at(0)
  const vuetifyEnd = vuetifyDates.at(-1)

  const start = new Temporal.PlainDate(vuetifyDateHelper.getYear(vuetifyStart), vuetifyDateHelper.getMonth(vuetifyStart) + 1, vuetifyDateHelper.getDate(vuetifyStart))
  const end = new Temporal.PlainDate(vuetifyDateHelper.getYear(vuetifyEnd), vuetifyDateHelper.getMonth(vuetifyEnd) + 1, vuetifyDateHelper.getDate(vuetifyEnd))

  const numDays = start.until(end).days + 1

  const dowsInDateRange = range(0, Math.min(7, numDays)).map(i => {
    const date = start.add({days: i})
    // Temporal.PlainDate.dayOfWeek returns 1-7 (1 = Monday), but Product.isEnabledOnAtLeastOneWeekday() expects 0-6 (0 = Sunday).
    return date.dayOfWeek === 7 ? 0 : date.dayOfWeek
  })

  if (props.locationProduct.product.isEnabledOnAtLeastOneWeekday(dowsInDateRange)) { // expects sunday = 0
    return true
  }

  return 'Product isn\'t active on any of the selected weekdays' // TODO: i18n

}

// @ts-ignore Don't complain about unused function
function isCheaperThanLongerCdrs(thisCdrCost: any, thisDefaultRate: DefaultRate, channelFacility: FormChannelFacility) {

  if (!productHasTieredRates.value) {
    return true
  }

  if (!thisCdrCost) {
    return true
  }

  const thisDuration = getDurationInHours(thisDefaultRate.perQuantity, thisDefaultRate.perUnit)
  if (thisDuration === null) {
    return true // shouldn't happen, but just in case
  }

  const isCheaper = defaultRates.value.every(thatDefaultRate => {

    if (thatDefaultRate.ID === thisDefaultRate.ID) {
      return true // this field is blank
    }

    const thatCdr = channelFacility.channelDefaultRates.get(thatDefaultRate.ID)

    if (!thatCdr || !thatCdr.cost) {
      return true // this CF has no CDR for this DR, or its cost is blank
    }

    const thatDuration = getDurationInHours(thatDefaultRate.perQuantity, thatDefaultRate.perUnit)
    return thatDuration === null || thatDuration < thisDuration || thatCdr.cost > thisCdrCost

  })

  return isCheaper || 'Must be cheaper than longer rate tiers'

}

/**
 * Raw default and spot rates
 */

const checkingForSpotRates = ref<boolean>(false)
const spotRatesAbortController = ref<null | AbortController>(null)

const existingSpotRates = computedAsync<GetRatesByLocationIdResponse['spot_rates']>(
  async () => {

    if (spotRatesAbortController.value) {
      spotRatesAbortController.value.abort()
      spotRatesAbortController.value = null
    }

    spotRatesAbortController.value = new AbortController()

    if (!formState.dateRange) {
      return []
    }

    checkingForSpotRates.value = true
    let output: GetRatesByLocationIdResponse['spot_rates'] = []

    await DefaultRate.getByLocationId({
      locationId: props.location.ID,
      vertical: props.locationProduct.product.verticalType,
      start: formState.dateRange.start,
      end: formState.dateRange.end,
      signal: spotRatesAbortController.value.signal,
    })
      .then((response: GetRatesByLocationIdResponse) => {
        output = response.spot_rates.filter(sr => sr.locationProductID === props.locationProduct.ID)
      })
      .catch(e => {
        console.error(e)
        checkingForSpotRates.value = false
      })
    .finally(() => {
      checkingForSpotRates.value = false
    })

    return output

  },
  [],
)

const existingParentSpotRateCount = computed<number>(() => {
  const grandparentSpotRates = Object.groupBy(existingSpotRates.value, sr => `${sr.startDate}_${sr.endDate}`)
  return Object.keys(grandparentSpotRates).length
})

const defaultRates = computed<DefaultRate[]>(() => {

  return DefaultRate.query()
    .where('locationProductID', props.locationProduct.ID)
    .with([
      'channelDefaultRates',
      'channelDefaultRates.channelFacility',
    ])
    .orderBy(dr => getDurationInHours((dr as DefaultRate).perQuantity, (dr as DefaultRate).perUnit) ?? 0)
    .get()

})

const today = Temporal.Now.plainDateISO()

watchEffect(() => {

  const oneDaySelected = formState.dateRange && formState.dateRange.start.until(formState.dateRange.end).days === 0
  const channelFacilitiesById = new Map<number, FormChannelFacility>()

  defaultRates.value.forEach(dr => {
    dr.channelDefaultRates.forEach(cdr => {
      const channelFacilityId = cdr.channelFacilitiesID
      if (!(channelFacilityId in channelFacilitiesById)) {
        channelFacilitiesById.set(channelFacilityId, {
          id: channelFacilityId,
          channel: cdr.channel,
          exChannelId: cdr?.channelFacility.externalFacilityID ?? null,
          channelDefaultRates: new Map<number, FormCdr>(),
        })
      }
    })
  })

  formState.channelFacilities = channelFacilitiesById.values().toArray().map(cf => {

    defaultRates.value.forEach(dr => {

      const cdr = dr.channelDefaultRates.find(cdr => cdr.channelFacilitiesID === cf.id)

      if (!cdr) {
        return
      }

      let cost = null

      if (oneDaySelected) {
        const existingSpotRate = existingSpotRates.value.find(sr => sr.channelFacilityID === cdr.channelFacilitiesID && sr.defaultRateID === dr.ID)
        cost = existingSpotRate ? existingSpotRate.spotCost : cdr.channelCost
      } else if (!existingSpotRates.value.length) {
        cost = cdr.channelCost
      }

      const formCdr: FormCdr = {
        id: cdr.ID,
        defaultCost: dr.defaultCost,
        channelCost: cdr.channelCost,
        cost: cost,
      }

      cf.channelDefaultRates.set(dr.ID, formCdr)

    })

    return cf

  })

})

/**
 * Submission
 */

const submitting = ref<boolean>(false)

async function submit() {

  // do additional validation that we can't do at form-filling-out time

  const tiersAreNotSequential = formState.channelFacilities.values().some(cf => {
    let previousTierCost = 0
    let theseTiersSuck = false
    cf.channelDefaultRates.forEach(cdr => {
      if (cdr.cost === null) {
        return
      }
      if (cdr.cost < previousTierCost) {
        theseTiersSuck = true
      }
      previousTierCost = cdr.cost
    })
    return theseTiersSuck
  })

  if (tiersAreNotSequential) {
    NotificationService.error('Can\'t have a tier that\'s cheaper than a shorter tier')
    return
  }

  submitting.value = true

  const request: SyncSpotRatesRequest = {
    spotRates: []
  }

  formState.channelFacilities.values().forEach(cf => {
    cf.channelDefaultRates.forEach( (cdr: FormCdr, defaultRateId: number) => {
      dates.value.forEach(d => {
        if (productIsLimitedByWeekday.value && !props.locationProduct.product.isEnabledOnWeekday(d.dayOfWeek === 7 ? 0 : d.dayOfWeek)) {
          return
        }
        if (!cdr.cost) {
          return
        }
        const item: SyncSpotRatesRequestItem = {
          date: d,
          locationProductId: props.locationProduct.ID,
          defaultRateId: defaultRateId,
          channelDefaultRateId: cdr.id,
          channelCost: cdr.cost!,
        }
        request.spotRates.push(item)
      })
    })
  })

  RatesApi.syncSpotRates(request)
    .then(() => {
      NotificationService.success('Rates updated')
      emit('submitted')
    })
    .catch(e => {
      console.error(e)
      NotificationService.error('Couldn\'t update rates!')
    })
    .finally(() => {
      submitting.value = false
    })

}

defineExpose({ submit, submitting })

/**
 * Miscellaneous
 */

function getDurationInHours(perQuantity: null | number, perUnit: null | string): null | number {
  if (perQuantity === null || perUnit === null) {
    return null
  }
  if (perUnit === 'day') {
    return perQuantity & 24
  } else if (perUnit === 'hour') {
    return perQuantity
  }
  throw new Error(`unsupported unit: "${perUnit}"`)
}

function setAllToDefault() {

  formState.channelFacilities.forEach(cf => {
    cf.channelDefaultRates.forEach(cdr => {
      cdr.cost = cdr.channelCost
    })
  })

}

</script>
